import { ApiError } from 'src/app/services/api-error';

export type PaymentFailureOutcome = 'declined' | 'unknown' | 'failed';

export interface PaymentFailure {
  outcome: PaymentFailureOutcome;
  message: string;
  status?: number;
}

const UNCONFIRMED_MESSAGE = 'We could not confirm whether your payment went through. Please wait a few minutes and refresh this page to check before trying again.';
const NOT_COMPLETED_MESSAGE = 'Your payment could not be completed. Please check your payment details and try again.';
const MAX_REASON_LENGTH = 300;
const UNCONFIRMED_STATUSES = new Set([0, 408, 409, 412]);
const SERVER_ERROR = 500;
const TECHNICAL_TEXT = /[<>{}\r\n]|exception/i;

// A lost connection, a timeout, a save conflict or a server error can arrive after the processor
// charged the payer, so only an answer the API gave on purpose may be shown as a decline.
export function describePaymentFailure(error: unknown): PaymentFailure {
  if (!(error instanceof ApiError)) {
    return { outcome: 'failed', message: NOT_COMPLETED_MESSAGE };
  }
  const { status } = error;
  if (UNCONFIRMED_STATUSES.has(status) || status >= SERVER_ERROR) {
    return { outcome: 'unknown', message: UNCONFIRMED_MESSAGE, status };
  }
  return { outcome: 'declined', message: displayableReason(error.serverMessage) ?? NOT_COMPLETED_MESSAGE, status };
}

function displayableReason(text: string | null): string | null {
  const reason = text?.trim();
  if (!reason || reason.length > MAX_REASON_LENGTH || TECHNICAL_TEXT.test(reason)) {
    return null;
  }
  return reason;
}

// Payment details refused before any request reaches the API, so no funds can have moved.
export function describeRejectedDetails(reason: string | null = null): PaymentFailure {
  return { outcome: 'declined', message: displayableReason(reason) ?? NOT_COMPLETED_MESSAGE };
}
